import { useState, useCallback } from 'react';
import type { ProductSummary } from '@arm-pd/domain';
import { useCatalogs, useCategories, useProducts } from '../hooks';
import { LaunchContext } from '../hooks/useCanvas';
import { CatalogSelector } from './CatalogSelector';
import { CategoryBrowser } from './CategoryBrowser';
import { SearchBar } from './SearchBar';
import { ProductCard } from './ProductCard';
import { ProductListSkeleton } from './Loading';
import { ErrorMessage, EmptyState } from './ErrorMessage';

/**
 * Product Discovery component props
 */
interface ProductDiscoveryProps {
  /**
   * Mode - 'live' for Canvas/Salesforce, 'mock' for development
   * @default 'mock'
   */
  mode?: 'live' | 'mock';

  /**
   * Launch context from Salesforce Canvas
   * Contains Quote/Order ID, currency, etc.
   */
  launchContext?: LaunchContext;
}

/**
 * Main Product Discovery component
 * Implements the complete discovery flow:
 * Catalog → Category → Browse/Search → Select
 *
 * Supports both:
 * - Canvas mode: Live ARM API calls with user context
 * - Mock mode: Development data without Salesforce
 */
export function ProductDiscovery({ mode = 'mock', launchContext }: ProductDiscoveryProps = {}) {
  const [selectedCatalogId, setSelectedCatalogId] = useState<string | undefined>(undefined);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedProduct, setSelectedProduct] = useState<ProductSummary | undefined>(undefined);

  // Use currency from launch context if available, otherwise default
  const currency = launchContext?.currencyCode || 'GBP';

  // Determine if we're in live mode
  const isLive = mode === 'live';

  const { data: catalogsData, isLoading: catalogsLoading, error: catalogsError } = useCatalogs();

  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useCategories(selectedCatalogId);

  const {
    data: productsData,
    isLoading: productsLoading,
    error: productsError,
  } = useProducts({
    catalogId: selectedCatalogId,
    categoryId: selectedCategoryId,
    search: searchTerm.trim() || undefined,
    currency: currency,
  });

  const handleCatalogSelect = useCallback((catalogId: string) => {
    setSelectedCatalogId(catalogId);
    setSelectedCategoryId(undefined);
    setSearchTerm('');
    setSelectedProduct(undefined);
  }, []);

  const handleCategorySelect = useCallback((categoryId: string | undefined) => {
    setSelectedCategoryId(categoryId);
    setSearchTerm('');
  }, []);

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term);
  }, []);

  const handleProductSelect = useCallback((product: ProductSummary) => {
    setSelectedProduct(product);
  }, []);

  const hasSelection = selectedCatalogId !== undefined;
  const isSearching = searchTerm.trim().length > 0;
  const products = productsData?.products ?? [];
  const totalProducts = productsData?.pagination.total ?? 0;

  // Add transaction context header if in Canvas mode
  const transactionInfo =
    isLive && launchContext ? (
      <div className="mb-4 rounded-lg bg-blue-50 px-4 py-3 border border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xs font-medium uppercase tracking-wider text-blue-600">
              {launchContext.transactionType} Context
            </span>
            <h2 className="text-lg font-semibold text-blue-900 mt-1">
              {launchContext.transactionId}
            </h2>
            {launchContext.pricebookId && (
              <p className="text-sm text-blue-700 mt-1">Pricebook: {launchContext.pricebookId}</p>
            )}
          </div>
          <div className="text-right">
            <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
              <span className="mr-1 h-2 w-2 rounded-full bg-green-500"></span>
              Live Data
            </span>
          </div>
        </div>
      </div>
    ) : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Product Discovery</h1>
              <p className="mt-1 text-sm text-gray-600">
                {isLive
                  ? 'Browse and search products with live ARM data'
                  : 'Browse and search products (development mode)'}
              </p>
            </div>
            {hasSelection && (
              <SearchBar
                value={searchTerm}
                onSearch={handleSearch}
                placeholder="Search products..."
                disabled={productsLoading}
              />
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Transaction context for Canvas mode */}
        {transactionInfo}

        {!hasSelection && (
          <CatalogSelector
            catalogs={catalogsData?.catalogs}
            selectedCatalogId={selectedCatalogId}
            onSelect={handleCatalogSelect}
            isLoading={catalogsLoading}
            error={catalogsError}
          />
        )}

        {hasSelection && (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
            <aside className="lg:col-span-1">
              <div className="sticky top-4 rounded-lg bg-white p-4 shadow-sm">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCatalogId(undefined);
                    setSelectedCategoryId(undefined);
                    setSearchTerm('');
                  }}
                  className="mb-4 flex items-center text-sm text-gray-600 hover:text-gray-900"
                >
                  <svg
                    className="mr-1 h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  Change Catalog
                </button>

                <div className="mb-4 border-b border-gray-200 pb-4">
                  <span className="text-xs font-medium uppercase tracking-wider text-gray-500">
                    Current Catalog
                  </span>
                  <p className="mt-1 text-lg font-medium text-gray-900">
                    {catalogsData?.catalogs.find(c => c.id === selectedCatalogId)?.name ??
                      selectedCatalogId}
                  </p>
                  {isLive && (
                    <p className="mt-1 text-xs text-green-600 flex items-center">
                      <span className="mr-1 h-1.5 w-1.5 rounded-full bg-green-500"></span>
                      Live ARM API
                    </p>
                  )}
                </div>

                <CategoryBrowser
                  categories={categoriesData?.categories}
                  selectedCategoryId={selectedCategoryId}
                  onSelect={handleCategorySelect}
                  isLoading={categoriesLoading}
                  error={categoriesError}
                />
              </div>
            </aside>

            <section className="lg:col-span-3">
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-medium text-gray-900">
                    {isSearching
                      ? `Search Results: "${searchTerm}"`
                      : selectedCategoryId
                        ? 'Products in Category'
                        : 'All Products'}
                  </h2>
                  {!productsLoading && (
                    <p className="mt-1 text-sm text-gray-600">
                      {totalProducts} {totalProducts === 1 ? 'product' : 'products'} found
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  {selectedCategoryId && (
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800">
                      {categoriesData?.categories.find(c => c.id === selectedCategoryId)?.name}
                      <button
                        type="button"
                        onClick={() => handleCategorySelect(undefined)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </span>
                  )}
                </div>
              </div>

              {productsError && (
                <ErrorMessage title="Failed to load products" message={productsError.message} />
              )}

              {productsLoading && <ProductListSkeleton />}

              {!productsLoading && !productsError && products.length === 0 && (
                <EmptyState
                  title={isSearching ? 'No products found' : 'No products available'}
                  message={
                    isSearching
                      ? `No products match "${searchTerm}". Try a different search term.`
                      : selectedCategoryId
                        ? 'This category has no products. Try another category.'
                        : 'This catalog has no products available.'
                  }
                />
              )}

              {!productsLoading && !productsError && products.length > 0 && (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {products.map(product => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onSelect={handleProductSelect}
                      isSelected={selectedProduct?.id === product.id}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </main>

      {selectedProduct && (
        <div className="fixed bottom-4 right-4 rounded-lg bg-blue-600 px-6 py-4 text-white shadow-lg">
          <p className="font-medium">{selectedProduct.name} selected</p>
          <p className="text-sm text-blue-100">Configuration coming in M2</p>
        </div>
      )}
    </div>
  );
}

export default ProductDiscovery;
